import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import {
  BarChart3,
  Eye,
  Plus,
  Trash2,
  Edit,
  MoreHorizontal,
  Database,
  Check,
  AlertCircle,
  X,
  ArrowUp,
  ArrowDown,
  LayoutDashboard,
  Mail,
  LayoutGrid,
} from "lucide-react";
import { DataService, supabase } from "../services/supabase";
import { SavedAnalysis, Dashboard } from "../types/analytics";
import { POST_SAVE_ACTIVE_DASHBOARD_KEY } from "../lib/reportingSession";
import { suggestUniqueDashboardName } from "../lib/reportingNames";
import AnalysisPreviewDialog from "../components/AnalysisPreviewDialog";
import DashboardViewDialog from "../components/DashboardViewDialog";
import AnalysisChartRenderer from "../components/AnalysisChartRenderer";
import { exportToSpreadsheet, exportToMultiSheetSpreadsheet, formatExportValue, exportToPDF, exportMultipleToPDF } from "../services/exportService";
import { DataJoinService } from "../services/dataJoins";
import { CalculationService } from "../services/calculationService";
import type {
  ActionableInsight,
  AutomatedReportFrequency,
  AutomatedReportJob,
  AutomatedReportType,
} from "../types/insights";
import { runInsightCrawler } from "../services/insightCrawlerService";
import {
  getEnrichedTransactionsForInsights,
  loadAutomatedReportsConfig,
  saveAutomatedReportsConfig,
  sendDashboardReportEmail,
  sendInsightsEmail,
} from "../services/reportingAutomationService";

interface ReportingProps {
  dataService?: DataService | null;
  onDataUpdate?: () => void;
  onAnalysesUpdate?: (analyses: SavedAnalysis[]) => void;
  onDashboardsUpdate?: (dashboards: Dashboard[]) => void;
  savedAnalyses?: SavedAnalysis[];
  dashboards?: Dashboard[];
  transactions?: any[];
  gradingOrders?: any[];
  expenses?: any[];
}

const Reporting = ({ dataService, onDataUpdate, onAnalysesUpdate, onDashboardsUpdate, savedAnalyses = [], dashboards = [], transactions = [], gradingOrders = [], expenses = [] }: ReportingProps) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("hub");
  const [selectedAnalyses, setSelectedAnalyses] = useState<Set<string>>(new Set());
  
  // Local state for saved analyses and dashboards (synced with props)
  const [localSavedAnalyses, setLocalSavedAnalyses] = useState<SavedAnalysis[]>(savedAnalyses);
  const [localDashboards, setLocalDashboards] = useState<Dashboard[]>(dashboards);
  
  // Sync local state with props when they change
  useEffect(() => {
    setLocalSavedAnalyses(savedAnalyses);
  }, [savedAnalyses]);
  
  useEffect(() => {
    setLocalDashboards(dashboards);
  }, [dashboards]);

  // Drop selections for analyses that no longer exist (e.g. after delete)
  useEffect(() => {
    const validIds = new Set(localSavedAnalyses.map((a) => a.id));
    setSelectedAnalyses((prev) => {
      const next = new Set(Array.from(prev).filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [localSavedAnalyses]);
  
  // State for create dashboard dialog
  const [createDashboardDialogOpen, setCreateDashboardDialogOpen] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState("");
  const [editingDashboard, setEditingDashboard] = useState<Dashboard | null>(null);
  const [activeWorkflowDashboardId, setActiveWorkflowDashboardId] = useState<string | null>(null);
  const [isWorkflowSavingDashboard, setIsWorkflowSavingDashboard] = useState(false);
  const [renameDashboardDialogOpen, setRenameDashboardDialogOpen] = useState(false);
  const [renameDashboardName, setRenameDashboardName] = useState("");
  const [renameDashboardError, setRenameDashboardError] = useState<string | null>(null);
  const [createDashboardError, setCreateDashboardError] = useState<string | null>(null);
  const [adhocDashboardId, setAdhocDashboardId] = useState("");
  const [editingReportJob, setEditingReportJob] = useState<AutomatedReportJob | null>(null);

  const [automatedReportJobs, setAutomatedReportJobs] = useState<AutomatedReportJob[]>([]);
  const [newReportType, setNewReportType] = useState<AutomatedReportType>("insights");
  const [newReportFrequency, setNewReportFrequency] = useState<AutomatedReportFrequency>("weekly");
  const [newReportDashboardId, setNewReportDashboardId] = useState<string>("");
  const [newReportEnabled, setNewReportEnabled] = useState(true);
  const [insightsSending, setInsightsSending] = useState(false);
  
  // State for preview/view dialogs
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [analysisToPreview, setAnalysisToPreview] = useState<SavedAnalysis | null>(null);
  const [dashboardViewDialogOpen, setDashboardViewDialogOpen] = useState(false);
  const [dashboardToView, setDashboardToView] = useState<Dashboard | null>(null);

  // Inline success/error message (replaces browser alerts)
  const [reportingMessage, setReportingMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const showReportingMessage = useCallback((type: "success" | "error", text: string) => {
    setReportingMessage({ type, text });
    setTimeout(() => setReportingMessage(null), 4000);
  }, []);

  const getErrorMessage = useCallback((error: unknown, fallback: string) => {
    if (error instanceof Error && error.message) return error.message;
    return fallback;
  }, []);

  const liveInsights = useMemo((): ActionableInsight[] => {
    const enriched = getEnrichedTransactionsForInsights(transactions, gradingOrders);
    return runInsightCrawler(enriched);
  }, [transactions, gradingOrders]);

  useEffect(() => {
    if (!dataService) return;
    let cancelled = false;
    (async () => {
      try {
        const jobs = await loadAutomatedReportsConfig(dataService);
        if (!cancelled) {
          setAutomatedReportJobs(jobs);
        }
      } catch (e) {
        console.error("Failed to load reporting automation preferences:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dataService]);

  useEffect(() => {
    if (newReportDashboardId || localDashboards.length === 0) return;
    setNewReportDashboardId(localDashboards[0].id);
  }, [localDashboards, newReportDashboardId]);

  const persistAutomatedJobs = useCallback(
    async (jobs: AutomatedReportJob[]) => {
      if (!dataService) {
        showReportingMessage("error", "Data service not available");
        return;
      }
      try {
        await saveAutomatedReportsConfig(dataService, jobs);
        setAutomatedReportJobs(jobs);
        showReportingMessage("success", "Automated reports saved");
      } catch (e) {
        showReportingMessage("error", getErrorMessage(e, "Failed to save automated reports"));
      }
    },
    [dataService, showReportingMessage, getErrorMessage]
  );

  const handleSendInsightEmailNow = useCallback(async () => {
    if (!dataService) return;
    setInsightsSending(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const to = user?.email;
      if (!to) throw new Error("You must be signed in to email reports");
      await sendInsightsEmail({
        userEmail: to,
        transactions,
        gradingOrders,
      });
      showReportingMessage("success", "Insight digest sent to your email.");
    } catch (e) {
      showReportingMessage("error", getErrorMessage(e, "Failed to send insight email"));
    } finally {
      setInsightsSending(false);
    }
  }, [dataService, transactions, gradingOrders, showReportingMessage, getErrorMessage]);
  
  // Sync dashboardToView when localDashboards changes (to reflect chart type updates)
  useEffect(() => {
    if (dashboardToView && localDashboards.length > 0) {
      const updatedDashboard = localDashboards.find(d => d.id === dashboardToView.id);
      if (updatedDashboard) {
        setDashboardToView(updatedDashboard);
      }
    }
  }, [localDashboards, dashboardToView]);
  
  // Sync analysisToPreview when localSavedAnalyses changes (to reflect chart type updates)
  useEffect(() => {
    if (analysisToPreview && localSavedAnalyses.length > 0) {
      const updatedAnalysis = localSavedAnalyses.find(a => a.id === analysisToPreview.id);
      if (updatedAnalysis) {
        setAnalysisToPreview(updatedAnalysis);
      }
    }
  }, [localSavedAnalyses, analysisToPreview]);
  
  // Sync editingDashboard when localDashboards changes (to reflect chart type updates)
  useEffect(() => {
    if (editingDashboard && localDashboards.length > 0) {
      const updatedDashboard = localDashboards.find(d => d.id === editingDashboard.id);
      if (updatedDashboard) {
        setEditingDashboard(updatedDashboard);
      }
    }
  }, [localDashboards, editingDashboard]);
  
  // State for delete dialogs
  const [deleteAnalysisDialogOpen, setDeleteAnalysisDialogOpen] = useState(false);
  const [analysisToDelete, setAnalysisToDelete] = useState<SavedAnalysis | null>(null);
  const [deleteDashboardDialogOpen, setDeleteDashboardDialogOpen] = useState(false);
  const [dashboardToDelete, setDashboardToDelete] = useState<Dashboard | null>(null);
  
  // Targeted refresh functions
  const refreshAnalyses = useCallback(async () => {
    if (!dataService) return;
    try {
      const updated = await dataService.getSavedAnalyses();
      setLocalSavedAnalyses(updated);
      // Update parent state without reloading everything
      if (onAnalysesUpdate) {
        onAnalysesUpdate(updated);
      }
    } catch (error) {
      console.error('Error refreshing analyses:', error);
    }
  }, [dataService, onAnalysesUpdate]);
  
  const refreshDashboards = useCallback(async () => {
    if (!dataService) return;
    try {
      const updated = await dataService.getDashboards();
      setLocalDashboards(updated);
      // Update parent state without reloading everything
      if (onDashboardsUpdate) {
        onDashboardsUpdate(updated);
      }
    } catch (error) {
      console.error('Error refreshing dashboards:', error);
    }
  }, [dataService, onDashboardsUpdate]);

  const createDashboardAnalysisLayout = useCallback((analysis: SavedAnalysis, index: number) => ({
    ...analysis,
    layout: {
      i: analysis.id,
      x: (index % 2) * 6,
      y: Math.floor(index / 2) * 4,
      w: 6,
      h: 4,
      minW: 3,
      minH: 3,
    }
  }), []);

  useEffect(() => {
    if (localDashboards.length === 0) return;
    if (activeWorkflowDashboardId && localDashboards.some((d) => d.id === activeWorkflowDashboardId)) {
      return;
    }
    const overview = localDashboards.find((d) => d.id === "overview-default");
    setActiveWorkflowDashboardId(overview?.id ?? localDashboards[0].id);
  }, [localDashboards, activeWorkflowDashboardId]);

  useEffect(() => {
    if (adhocDashboardId || localDashboards.length === 0) return;
    setAdhocDashboardId(localDashboards[0].id);
  }, [localDashboards, adhocDashboardId]);
  
  // Helper functions
  const activeWorkflowDashboard = activeWorkflowDashboardId
    ? localDashboards.find((dashboard) => dashboard.id === activeWorkflowDashboardId) || null
    : null;

  // Get aggregated chart data for an analysis (matching what's displayed in the chart)
  const getAggregatedChartData = useCallback((analysis: SavedAnalysis): any[] => {
    // Reuse the same logic as AnalysisChartRenderer to get the chart data
    // We need to process the same way the chart does
    
    let sourceData: any[] = [];
    
    // Get the appropriate data source
    if (analysis.dataSource === 'joined_transactions' || analysis.dataSource === 'transactions') {
      sourceData = DataJoinService.joinTransactionsWithGradingOrders(transactions, gradingOrders);
      sourceData = CalculationService.enrichTransactions(sourceData, gradingOrders);
    } else if (analysis.dataSource === 'gradingOrders') {
      sourceData = [...gradingOrders];
      sourceData = CalculationService.enrichGradingOrders(sourceData, transactions);
    } else if (analysis.dataSource === 'expenses') {
      sourceData = [...expenses];
    }

    // Apply filters
    let filteredData = sourceData.filter((item: any) => {
      if (!analysis.filters) return true;
      
      for (const [key, filterValue] of Object.entries(analysis.filters)) {
        if (filterValue === undefined || filterValue === null || filterValue === '') continue;
        
        const itemValue = (item as any)[key];
        
        if (Array.isArray(filterValue)) {
          if (filterValue.length > 0 && !filterValue.includes(itemValue)) {
            return false;
          }
        } else if (itemValue !== filterValue) {
          return false;
        }
      }
      
      return true;
    });

    // Apply time range filter if specified
    if (analysis.timeRange && analysis.timeRange.enabled) {
      const timeField = analysis.metrics?.[0]?.includes('sale') ? 'sale_date' : 
                       analysis.metrics?.[0]?.includes('purchase') ? 'purchase_date' :
                       analysis.groupBy?.find(g => g.includes('date') || g.includes('Date'))?.replace('_grouped', '') || 'date';
      
      filteredData = filteredData.filter((item: any) => {
        const itemDate = new Date((item as any)[timeField]);
        const startDate = new Date(analysis.timeRange!.start);
        const endDate = new Date(analysis.timeRange!.end);
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    // Now aggregate the data similar to AnalysisChartRenderer
    const metrics = analysis.metrics || [];
    const categoricals = analysis.groupBy || [];
    
    // Check for time dimension
    const groupedDateField = categoricals.find((cat: string) => 
      cat.endsWith('_grouped') && (cat.includes('date') || cat.includes('Date'))
    );
    const hasTrend = (analysis.timeGranularity && analysis.timeGranularity !== 'none') || !!groupedDateField;
    const granularity = analysis.timeGranularity || 'month';

    // Helper to format date by granularity
    const formatDateByGranularity = (date: Date, gran: string): string => {
      const format = (d: Date, fmt: string) => {
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        if (fmt === 'week') {
          const week = Math.ceil((d.getDate() + new Date(year, d.getMonth(), 1).getDay()) / 7);
          return `${year}-W${String(week).padStart(2, '0')}`;
        } else if (fmt === 'quarter') {
          const quarter = Math.floor(d.getMonth() / 3) + 1;
          return `${year}-Q${quarter}`;
        } else if (fmt === 'year') {
          return String(year);
        } else {
          return `${year}-${month}`;
        }
      };
      return format(date, gran);
    };

    // Add grouped date fields
    const enrichedData = filteredData.map((item: any) => {
      const result: any = { ...item };
      if (hasTrend && granularity && granularity !== 'none') {
        let trendField = 'purchase_date';
        if (groupedDateField) {
          trendField = groupedDateField.replace('_grouped', '');
        } else {
          const saleMetrics = ['sale_price', 'calculated_net_profit', 'roi', 'count_sold'];
          const purchaseMetrics = ['cost', 'calculated_total_cost', 'count_purchased'];
          if (metrics.some((m: string) => saleMetrics.includes(m))) {
            trendField = 'sale_date';
          } else if (metrics.some((m: string) => purchaseMetrics.includes(m))) {
            trendField = 'purchase_date';
          }
        }
        
        const groupedFieldName = `${trendField}_grouped`;
        if (!result[groupedFieldName]) {
          const dateStr = item[trendField];
          if (dateStr) {
            const date = new Date(dateStr);
            if (!isNaN(date.getTime())) {
              result[groupedFieldName] = formatDateByGranularity(date, granularity);
            }
          }
        }
      }
      return result;
    });

    // Helper to aggregate metrics
    const aggregateMetric = (items: any[], metric: string): number => {
      if (metric.startsWith('count_')) {
        return items.length;
      }
      
      const values = items.map((item: any) => {
        const value = item[metric];
        if (value === null || value === undefined) return 0;
        if (typeof value === 'number') return value;
        if (typeof value === 'string' && value.includes('$')) {
          return parseFloat(value.replace(/[$,]/g, '')) || 0;
        }
        return parseFloat(value) || 0;
      });
      
      return values.reduce((sum, val) => sum + val, 0);
    };

    // Separate time dimensions from categorical dimensions
    const timeDimensions = categoricals.filter((cat: string) => 
      cat.endsWith('_grouped') && (cat.includes('date') || cat.includes('Date'))
    );
    const nonTimeDimensions = categoricals.filter((cat: string) => !timeDimensions.includes(cat));

    const isStackedBar = (analysis.chartType as string) === 'stacked_bar';
    const isMultipleLine = (analysis.chartType as string) === 'multiple_line';

    if (isStackedBar || isMultipleLine) {
      // Stacked bar or multiple line: need to group by both x-axis and stack dimension
      let xAxisDimension: string | null = null;
      let stackDimension: string | null = null;

      if (hasTrend) {
        const timeDim = timeDimensions.length > 0 ? timeDimensions[0] : `${enrichedData[0] ? Object.keys(enrichedData[0]).find(k => k.endsWith('_grouped')) : null}`;
        xAxisDimension = timeDim || null;
        stackDimension = nonTimeDimensions.length > 0 ? nonTimeDimensions[0] : null;
      } else {
        xAxisDimension = nonTimeDimensions.length > 0 ? nonTimeDimensions[0] : null;
        stackDimension = nonTimeDimensions.length > 1 ? nonTimeDimensions[1] : null;
      }

      if (!xAxisDimension || !stackDimension) {
        // Fallback to regular grouping
        const groups: Record<string, any[]> = {};
        enrichedData.forEach((item: any) => {
          const keys: string[] = [];
          nonTimeDimensions.forEach((cat: string) => {
            keys.push((item as any)[cat] || '');
          });
          if (hasTrend && timeDimensions.length > 0) {
            timeDimensions.forEach((timeDim: string) => {
              keys.push((item as any)[timeDim] || 'Unknown');
            });
          }
          const groupKey = keys.length > 0 ? keys.join('|') : 'All';
          if (!groups[groupKey]) {
            groups[groupKey] = [];
          }
          groups[groupKey].push(item);
        });

        return Object.entries(groups)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([key, items]) => {
            const groupParts = key.split('|');
            const record: any = {};
            groupParts.forEach((part, idx) => {
              if (hasTrend && idx === groupParts.length - 1) {
                record[timeDimensions[0]?.replace('_grouped', '') || 'Time'] = part;
              } else {
                record[nonTimeDimensions[idx] || `Dimension ${idx + 1}`] = part;
              }
            });
            metrics.forEach((metric: string) => {
              record[metric] = aggregateMetric(items, metric);
            });
            return record;
          });
      }

      // Group by x-axis and stack
      const xAxisGroups: Record<string, Record<string, any[]>> = {};
      
      enrichedData.forEach((item: any) => {
        const xValue = (item as any)[xAxisDimension!] || 'Unknown';
        const stackValue = (item as any)[stackDimension!] || 'Unknown';
        
        if (!xAxisGroups[xValue]) {
          xAxisGroups[xValue] = {};
        }
        if (!xAxisGroups[xValue][stackValue]) {
          xAxisGroups[xValue][stackValue] = [];
        }
        xAxisGroups[xValue][stackValue].push(item);
      });

      // Build result with x-axis as rows and stack values as columns
      const sortedStackValues = Array.from(new Set(
        Object.values(xAxisGroups).flatMap(groups => Object.keys(groups))
      )).sort();

      const result: any[] = [];
      Object.entries(xAxisGroups)
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([xValue, stackGroups]) => {
          const record: any = {};
          
          // Add x-axis dimension as first column
          record[xAxisDimension!.replace('_grouped', '') || 'X-Axis'] = xValue;
          
          // Add each stack value as a column with aggregated metric
          metrics.forEach((metric: string) => {
            sortedStackValues.forEach((stackValue) => {
              const items = stackGroups[stackValue] || [];
              const columnName = stackValue;
              record[columnName] = aggregateMetric(items, metric);
            });
          });

          result.push(record);
        });

      return result;
    } else {
      // Regular grouping (non-stacked)
      const groups: Record<string, any[]> = {};
      
      enrichedData.forEach((item: any) => {
        const keys: string[] = [];

        nonTimeDimensions.forEach((cat: string) => {
          keys.push((item as any)[cat] || '');
        });

        if (hasTrend) {
          if (timeDimensions.length > 0) {
            timeDimensions.forEach((timeDim: string) => {
              keys.push((item as any)[timeDim] || 'Unknown');
            });
          } else {
            // Fallback
            const trendField = enrichedData[0] ? Object.keys(enrichedData[0]).find(k => k.includes('date') && !k.includes('_grouped')) : 'date';
            if (trendField) {
              const dateStr = item[trendField];
              if (dateStr) {
                const date = new Date(dateStr);
                if (!isNaN(date.getTime())) {
                  keys.push(formatDateByGranularity(date, granularity));
                } else {
                  keys.push('Unknown');
                }
              } else {
                keys.push('Unknown');
              }
            }
          }
        }

        const groupKey = keys.length > 0 ? keys.join('|') : 'All';

        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }
        groups[groupKey].push(item);
      });

      return Object.entries(groups)
        .sort(([a], [b]) => {
          if (hasTrend && timeDimensions.length > 0) {
            const timeParts = a.split('|');
            const timeA = timeParts[timeParts.length - 1] || a;
            const timePartsB = b.split('|');
            const timeB = timePartsB[timePartsB.length - 1] || b;
            return timeA.localeCompare(timeB);
          }
          return a.localeCompare(b);
        })
        .map(([key, items]) => {
          const groupParts = key.split('|');
          const record: any = {};
          
          // Add groupBy dimensions as columns
          let dimensionIdx = 0;
          nonTimeDimensions.forEach((cat: string) => {
            record[cat] = groupParts[dimensionIdx] || '';
            dimensionIdx++;
          });
          
          if (hasTrend && timeDimensions.length > 0) {
            timeDimensions.forEach((timeDim: string) => {
              record[timeDim.replace('_grouped', '') || 'Time'] = groupParts[dimensionIdx] || 'Unknown';
              dimensionIdx++;
            });
          }
          
          // Add metrics as columns
          metrics.forEach((metric: string) => {
            record[metric] = aggregateMetric(items, metric);
          });

          return record;
        });
    }
  }, [transactions, gradingOrders, expenses]);

  const handleSendDashboardReportNow = useCallback(
    async (dashboard: Dashboard) => {
      if (!dataService) return;
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const to = user?.email;
        if (!to) throw new Error("You must be signed in to email reports");
        const merged = dashboard.analyses.map((a) => localSavedAnalyses.find((s) => s.id === a.id) || a);
        const sheets = merged.map((analysis) => ({
          name: analysis.name || analysis.id,
          rows: getAggregatedChartData(analysis) as Record<string, unknown>[],
        }));
        await sendDashboardReportEmail({
          userEmail: to,
          dashboardName: dashboard.name,
          sheets,
        });
        showReportingMessage("success", `Report for "${dashboard.name}" emailed`);
      } catch (e) {
        showReportingMessage("error", getErrorMessage(e, "Failed to send dashboard report"));
      }
    },
    [dataService, localSavedAnalyses, getAggregatedChartData, showReportingMessage, getErrorMessage]
  );

  const handleAddAutomatedReportJob = useCallback(() => {
    if (newReportType === "dashboard" && !newReportDashboardId) {
      showReportingMessage("error", "Select a dashboard for this scheduled report");
      return;
    }
    const job: AutomatedReportJob = {
      id: `job-${Date.now()}`,
      type: newReportType,
      enabled: newReportEnabled,
      frequency: newReportFrequency,
      dashboardId: newReportType === "dashboard" ? newReportDashboardId : undefined,
    };
    void persistAutomatedJobs([...automatedReportJobs, job]);
  }, [
    newReportType,
    newReportDashboardId,
    newReportEnabled,
    newReportFrequency,
    automatedReportJobs,
    persistAutomatedJobs,
    showReportingMessage,
  ]);

  // Get underlying data for an analysis (raw records, not aggregated) - kept for backward compatibility
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getAnalysisData = useCallback((analysis: SavedAnalysis): any[] => {
    let sourceData: any[] = [];
    
    // Get the appropriate data source
    if (analysis.dataSource === 'joined_transactions' || analysis.dataSource === 'transactions') {
      // Join transactions with grading orders
      sourceData = DataJoinService.joinTransactionsWithGradingOrders(transactions, gradingOrders);
      // Enrich with calculated fields
      sourceData = CalculationService.enrichTransactions(sourceData, gradingOrders);
    } else if (analysis.dataSource === 'gradingOrders') {
      sourceData = [...gradingOrders];
      sourceData = CalculationService.enrichGradingOrders(sourceData, transactions);
    } else if (analysis.dataSource === 'expenses') {
      sourceData = [...expenses];
    }

    // Apply filters
    let filteredData = sourceData.filter((item: any) => {
      if (!analysis.filters) return true;
      
      for (const [key, filterValue] of Object.entries(analysis.filters)) {
        if (filterValue === undefined || filterValue === null || filterValue === '') continue;
        
        const itemValue = (item as any)[key];
        
        // Handle array filters (e.g., sport: ['NBA', 'MLB'])
        if (Array.isArray(filterValue)) {
          if (filterValue.length > 0 && !filterValue.includes(itemValue)) {
            return false;
          }
        } 
        // Handle exact match filters
        else if (itemValue !== filterValue) {
          return false;
        }
      }
      
      return true;
    });

    // Apply time range filter if specified
    if (analysis.timeRange && analysis.timeRange.enabled) {
      const timeField = analysis.metrics?.[0]?.includes('sale') ? 'sale_date' : 
                       analysis.metrics?.[0]?.includes('purchase') ? 'purchase_date' :
                       analysis.groupBy?.find(g => g.includes('date') || g.includes('Date')) || 'date';
      
      filteredData = filteredData.filter((item: any) => {
        const itemDate = new Date((item as any)[timeField]);
        const startDate = new Date(analysis.timeRange!.start);
        const endDate = new Date(analysis.timeRange!.end);
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    return filteredData;
  }, [transactions, gradingOrders, expenses]);

  // Export analysis data to spreadsheet (aggregated chart data)
  const handleExportAnalysisData = useCallback((analysis: SavedAnalysis) => {
    try {
      const data = getAggregatedChartData(analysis);
      
      if (data.length === 0) {
        showReportingMessage('error', 'No data available to export for this analysis');
        return;
      }

      // Format the aggregated data for export
      const exportData = data.map((item: any) => {
        const row: any = {};
        
        // Include all fields from the aggregated data
        Object.keys(item).forEach(key => {
          row[key] = formatExportValue(item[key]);
        });
        
        return row;
      });

      exportToSpreadsheet(exportData, {
        filename: `analysis-${analysis.name.replace(/[^a-zA-Z0-9]/g, '-')}`,
        includeDateInFilename: true,
      });
    } catch (error) {
      console.error('Error exporting analysis data:', error);
      showReportingMessage('error', 'Failed to export analysis data. Please try again.');
    }
  }, [getAggregatedChartData, showReportingMessage]);

  // Export analysis to PDF
  const handleExportAnalysisPDF = useCallback(async (analysis: SavedAnalysis, chartElementRef?: HTMLElement | null) => {
    try {
      if (!chartElementRef) {
        showReportingMessage('error', 'Chart element not available. Please try again.');
        return;
      }

      await exportToPDF({
        filename: `analysis-${analysis.name.replace(/[^a-zA-Z0-9]/g, '-')}`,
        title: analysis.name,
        content: {
          type: 'chart',
          chartElement: chartElementRef,
        },
        includeDateInFilename: true,
      });
    } catch (error) {
      console.error('Error exporting analysis PDF:', error);
      showReportingMessage('error', 'Failed to export PDF. Please try again.');
    }
  }, [showReportingMessage]);

  // Export dashboard to PDF
  const handleExportDashboardPDF = useCallback(async (dashboard: Dashboard, chartElements: Array<{ analysisId: string; element: HTMLElement }>) => {
    try {
      if (chartElements.length === 0) {
        showReportingMessage('error', 'No charts available to export');
        return;
      }

      // Merge with latest analysis data
      const mergedAnalyses = dashboard.analyses.map(analysisInDashboard => {
        const latestAnalysis = localSavedAnalyses.find(a => a.id === analysisInDashboard.id);
        return latestAnalysis || analysisInDashboard;
      });

      // Create pages for each chart
      const pages = chartElements.map(({ analysisId }) => {
        const analysis = mergedAnalyses.find(a => a.id === analysisId);
        const chartElement = chartElements.find(ce => ce.analysisId === analysisId)?.element;
        
        return {
          title: analysis?.name || 'Unknown Analysis',
          chartElement: chartElement,
        };
      });

      await exportMultipleToPDF(pages, `dashboard-${dashboard.name.replace(/[^a-zA-Z0-9]/g, '-')}`, true);
    } catch (error) {
      console.error('Error exporting dashboard PDF:', error);
      showReportingMessage('error', 'Failed to export PDF. Please try again.');
    }
  }, [localSavedAnalyses, showReportingMessage]);

  // Export dashboard data to Excel (multi-sheet)
  const handleExportDashboardData = useCallback((dashboard: Dashboard) => {
    try {
      // Merge with latest analysis data to ensure chart types are current
      const mergedAnalyses = dashboard.analyses.map(analysisInDashboard => {
        const latestAnalysis = localSavedAnalyses.find(a => a.id === analysisInDashboard.id);
        return latestAnalysis || analysisInDashboard;
      });

      if (mergedAnalyses.length === 0) {
        showReportingMessage('error', 'No analyses in this dashboard to export');
        return;
      }

      // Create sheets for each analysis (using aggregated chart data)
      const sheets = mergedAnalyses.map(analysis => {
        const data = getAggregatedChartData(analysis);
        
        // Format the aggregated data for export
        const exportData = data.map((item: any) => {
          const row: any = {};
          
          // Include all fields from the aggregated data
          Object.keys(item).forEach(key => {
            row[key] = formatExportValue(item[key]);
          });
          
          return row;
        });

        return {
          name: analysis.name || `Analysis ${analysis.id}`,
          data: exportData,
        };
      });

      exportToMultiSheetSpreadsheet(sheets, {
        filename: `dashboard-${dashboard.name.replace(/[^a-zA-Z0-9]/g, '-')}`,
        includeDateInFilename: true,
      });
    } catch (error) {
      console.error('Error exporting dashboard data:', error);
      showReportingMessage('error', 'Failed to export dashboard data. Please try again.');
    }
  }, [localSavedAnalyses, getAggregatedChartData, showReportingMessage]);
  
  const getAnalysisTypeIcon = (chartType: string) => {
    switch (chartType) {
      case 'bar': return '📊';
      case 'line': return '📈';
      case 'pie': return '🥧';
      case 'table': return '📋';
      case 'summary': return '📄';
      case 'stacked_bar': return '📊';
      case 'multiple_line': return '📈';
      default: return '📊';
    }
  };
  
  // Determine valid chart types for a given analysis based on data shape
  const getValidChartTypes = (analysis: SavedAnalysis): string[] => {
    const metrics = analysis.metrics || [];
    const categoricals = analysis.groupBy || [];
    
    // Check if there's a time dimension
    const hasGroupedDateField = categoricals.some(cat => 
      cat.endsWith('_grouped') && (cat.includes('date') || cat.includes('Date'))
    );
    const hasTrend = (analysis.timeGranularity && analysis.timeGranularity !== 'none') || hasGroupedDateField;
    
    // Separate time dimensions from categorical dimensions
    const timeDimensions = categoricals.filter(cat => 
      cat.endsWith('_grouped') && (cat.includes('date') || cat.includes('Date'))
    );
    const nonTimeDimensions = categoricals.filter(cat => !timeDimensions.includes(cat));
    
    const validTypes: string[] = [];
    
    // 1 metric, 0 categoricals, 0 trends → scalar (no alternatives)
    if (metrics.length === 1 && !hasTrend && categoricals.length === 0) {
      return ['scalar'];
    }
    
    // 1 metric, 0 categoricals, 1 trend → line (can also be bar)
    if (metrics.length === 1 && hasTrend && nonTimeDimensions.length === 0) {
      validTypes.push('line', 'bar');
      return validTypes;
    }
    
    // 1 metric, 1 categorical, 0 trends → bar or pie
    if (metrics.length === 1 && !hasTrend && nonTimeDimensions.length === 1) {
      validTypes.push('bar', 'pie');
      return validTypes;
    }
    
    // 1 metric, 2 categoricals, 0 trends → stacked_bar
    if (metrics.length === 1 && !hasTrend && nonTimeDimensions.length === 2) {
      validTypes.push('stacked_bar');
      return validTypes;
    }
    
    // 1 metric, 1+ categoricals, 1 trend → stacked_bar (can also be multiple_line)
    if (metrics.length === 1 && hasTrend && nonTimeDimensions.length > 0) {
      validTypes.push('stacked_bar', 'multiple_line');
      return validTypes;
    }
    
    // multiple metrics, 1 trend, 0 categoricals → multiple_line (can also be stacked_bar with grouping)
    if (metrics.length > 1 && hasTrend && nonTimeDimensions.length === 0) {
      validTypes.push('multiple_line', 'stacked_bar');
      return validTypes;
    }
    
    // multiple metrics, 0 trends, 1+ categoricals → bar
    if (metrics.length > 1 && !hasTrend && nonTimeDimensions.length > 0) {
      validTypes.push('bar');
      return validTypes;
    }
    
    // Default fallback
    validTypes.push('bar');
    return validTypes;
  };
  
  // Handle chart type change
  const handleChartTypeChange = useCallback(async (analysis: SavedAnalysis, newChartType: string) => {
    if (!dataService) return;
    try {
      await dataService.updateSavedAnalysis(analysis.id, {
        chartType: newChartType as any
      });
      await refreshAnalyses();
      await refreshDashboards();
    } catch (error) {
      console.error('Error updating chart type:', error);
    }
  }, [dataService, refreshAnalyses, refreshDashboards]);
  

  
  const handleCreateNewDashboard = useCallback(async () => {
    if (!dataService) {
      showReportingMessage("error", "Data service not available");
      return;
    }
    setIsWorkflowSavingDashboard(true);
    try {
      const newDashboard = {
        id: `dashboard-${Date.now()}`,
        name: suggestUniqueDashboardName(localDashboards),
        analyses: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await dataService.saveDashboard(newDashboard);
      await refreshDashboards();
      setActiveWorkflowDashboardId(newDashboard.id);
      showReportingMessage("success", "Dashboard created");
    } catch (error) {
      console.error("Error creating dashboard:", error);
      showReportingMessage("error", getErrorMessage(error, "Failed to create dashboard"));
    } finally {
      setIsWorkflowSavingDashboard(false);
    }
  }, [dataService, localDashboards, refreshDashboards, showReportingMessage, getErrorMessage]);

  const handleRenameActiveDashboard = useCallback(async () => {
    if (!dataService || !activeWorkflowDashboard || !renameDashboardName.trim()) return;
    setRenameDashboardError(null);
    try {
      await dataService.updateDashboard(activeWorkflowDashboard.id, {
        name: renameDashboardName.trim(),
      });
      await refreshDashboards();
      setRenameDashboardDialogOpen(false);
      setRenameDashboardError(null);
      showReportingMessage("success", "Dashboard renamed");
    } catch (error) {
      setRenameDashboardError(getErrorMessage(error, "Failed to rename dashboard"));
    }
  }, [dataService, activeWorkflowDashboard, renameDashboardName, refreshDashboards, showReportingMessage, getErrorMessage]);

  const handleRemoveFromDashboard = useCallback(
    async (analysisId: string) => {
      if (!dataService || !activeWorkflowDashboard) return;
      const nextAnalyses = activeWorkflowDashboard.analyses.filter((a) => a.id !== analysisId);
      try {
        await dataService.updateDashboard(activeWorkflowDashboard.id, { analyses: nextAnalyses });
        await refreshDashboards();
        showReportingMessage("success", "Removed from dashboard");
      } catch (error) {
        showReportingMessage("error", getErrorMessage(error, "Failed to remove analysis"));
      }
    },
    [dataService, activeWorkflowDashboard, refreshDashboards, showReportingMessage, getErrorMessage]
  );

  const handleAddAnalysisToDashboard = useCallback(
    async (analysis: SavedAnalysis) => {
      if (!dataService || !activeWorkflowDashboard) {
        showReportingMessage("error", "Select a dashboard first");
        return;
      }
      if (activeWorkflowDashboard.analyses.some((a) => a.id === analysis.id)) {
        showReportingMessage("error", "This analysis is already on the dashboard");
        return;
      }
      const nextAnalyses = [
        ...activeWorkflowDashboard.analyses,
        createDashboardAnalysisLayout(analysis, activeWorkflowDashboard.analyses.length),
      ];
      try {
        await dataService.updateDashboard(activeWorkflowDashboard.id, { analyses: nextAnalyses });
        await refreshDashboards();
        showReportingMessage("success", `Added "${analysis.name}" to ${activeWorkflowDashboard.name}`);
      } catch (error) {
        showReportingMessage("error", getErrorMessage(error, "Failed to add analysis to dashboard"));
      }
    },
    [dataService, activeWorkflowDashboard, createDashboardAnalysisLayout, refreshDashboards, showReportingMessage, getErrorMessage]
  );

  const handleSendJobNow = useCallback(
    async (job: AutomatedReportJob) => {
      if (job.type === "insights") {
        await handleSendInsightEmailNow();
        return;
      }
      const dashboard = localDashboards.find((d) => d.id === job.dashboardId);
      if (dashboard) await handleSendDashboardReportNow(dashboard);
    },
    [handleSendInsightEmailNow, localDashboards, handleSendDashboardReportNow]
  );

  const moveAnalysisInActiveDashboard = useCallback(async (analysisId: string, direction: 'up' | 'down') => {
    if (!dataService || !activeWorkflowDashboard) return;

    const currentIndex = activeWorkflowDashboard.analyses.findIndex((analysis) => analysis.id === analysisId);
    if (currentIndex < 0) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= activeWorkflowDashboard.analyses.length) return;

    const nextAnalyses = [...activeWorkflowDashboard.analyses];
    const [moved] = nextAnalyses.splice(currentIndex, 1);
    nextAnalyses.splice(targetIndex, 0, moved);

    const relaidOut = nextAnalyses.map((analysis, index) => ({
      ...analysis,
      layout: {
        ...analysis.layout,
        i: analysis.id,
        x: (index % 3) * 4,
        y: Math.floor(index / 3) * 4,
        w: analysis.layout?.w || 4,
        h: analysis.layout?.h || 4,
        minW: analysis.layout?.minW || 2,
        minH: analysis.layout?.minH || 2,
      }
    }));

    try {
      await dataService.updateDashboard(activeWorkflowDashboard.id, { analyses: relaidOut });
      await refreshDashboards();
    } catch (error) {
      console.error('Error reordering dashboard analyses:', error);
      showReportingMessage('error', getErrorMessage(error, 'Failed to reorder dashboard cards'));
    }
  }, [dataService, activeWorkflowDashboard, refreshDashboards, showReportingMessage, getErrorMessage]);

  const openCreateAnalysisPage = useCallback(() => {
    if (typeof window !== "undefined" && activeWorkflowDashboard) {
      sessionStorage.setItem(POST_SAVE_ACTIVE_DASHBOARD_KEY, activeWorkflowDashboard.id);
    }
    navigate("/reporting/analysis/new");
  }, [navigate, activeWorkflowDashboard]);

  // Handle creating dashboard from selected analyses
  const handleCreateDashboard = useCallback(async () => {
    if (!dataService) {
      showReportingMessage('error', 'Data service not available');
      return;
    }
    
    if (!newDashboardName.trim() || selectedAnalyses.size === 0) {
      setCreateDashboardError('Please provide a dashboard name and select at least one analysis');
      return;
    }

    setCreateDashboardError(null);
    try {
      const selectedAnalysisData = localSavedAnalyses.filter(a => selectedAnalyses.has(a.id));
      
      // Create dashboard analyses with default layout
      const selectedAnalysisObjects = selectedAnalysisData.map((analysis, index) => ({
        ...analysis,
        layout: {
          i: analysis.id,
          x: (index % 3) * 4,
          y: Math.floor(index / 3) * 4,
          w: 4,
          h: 4,
          minW: 2,
          minH: 2,
        }
      }));
      
      const newDashboard = {
        id: Date.now().toString(),
        name: newDashboardName.trim(),
        analyses: selectedAnalysisObjects,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      
      if (editingDashboard) {
        // Update existing dashboard
        await dataService.updateDashboard(editingDashboard.id, {
          name: newDashboardName.trim(),
          analyses: selectedAnalysisObjects
        });
        await refreshDashboards();
        setCreateDashboardDialogOpen(false);
        setNewDashboardName('');
        setSelectedAnalyses(new Set());
        setEditingDashboard(null);
      } else {
        // Create new dashboard
        await dataService.saveDashboard(newDashboard);
        await refreshDashboards();
        setCreateDashboardDialogOpen(false);
        setNewDashboardName('');
        setSelectedAnalyses(new Set());
      }
    } catch (error) {
      console.error('Error creating dashboard:', error);
      setCreateDashboardError(getErrorMessage(error, 'Failed to create dashboard'));
    }
  }, [dataService, newDashboardName, selectedAnalyses, localSavedAnalyses, editingDashboard, refreshDashboards, showReportingMessage, getErrorMessage]);
  
  // Handle deleting an analysis
  const handleDeleteAnalysis = useCallback(async () => {
    if (!dataService || !analysisToDelete) {
      return;
    }
    
    try {
      const deletedId = analysisToDelete.id;
      await dataService.deleteSavedAnalysis(deletedId);
      setSelectedAnalyses((prev) => {
        const next = new Set(prev);
        next.delete(deletedId);
        return next;
      });
      await refreshAnalyses();
      setDeleteAnalysisDialogOpen(false);
      setAnalysisToDelete(null);
    } catch (error) {
      console.error('Error deleting analysis:', error);
      showReportingMessage('error', 'Failed to delete analysis');
    }
  }, [dataService, analysisToDelete, refreshAnalyses, showReportingMessage]);
  
  // Handle deleting a dashboard
  const handleDeleteDashboard = useCallback(async () => {
    if (!dataService || !dashboardToDelete) {
      return;
    }
    
    try {
      await dataService.deleteDashboard(dashboardToDelete.id);
      await refreshDashboards();
      setDeleteDashboardDialogOpen(false);
      setDashboardToDelete(null);
    } catch (error) {
      console.error('Error deleting dashboard:', error);
      showReportingMessage('error', 'Failed to delete dashboard');
    }
  }, [dataService, dashboardToDelete, refreshDashboards, showReportingMessage]);
  
  // Handle opening edit analysis
  const handleEditAnalysis = useCallback((analysis: SavedAnalysis) => {
    navigate(`/reporting/analysis/edit/${encodeURIComponent(analysis.id)}`);
  }, [navigate]);
  
  // Handle opening preview
  const handlePreviewAnalysis = useCallback((analysis: SavedAnalysis) => {
    setAnalysisToPreview(analysis);
    setPreviewDialogOpen(true);
  }, []);
  
  // Handle opening dashboard view
  const handleViewDashboard = useCallback((dashboard: Dashboard) => {
    setDashboardToView(dashboard);
    setDashboardViewDialogOpen(true);
  }, []);
  
  // Toggle analysis selection
  const toggleAnalysisSelection = useCallback((analysisId: string) => {
    setSelectedAnalyses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(analysisId)) {
        newSet.delete(analysisId);
      } else {
        newSet.add(analysisId);
      }
      return newSet;
    });
  }, []);
  
  return (
    <div className="min-h-screen bg-background">
      <main className="container py-8 space-y-8">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Reporting</h1>
            <p className="text-muted-foreground mt-1">
              Analytics and custom reports
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate("/reporting/analysis/new")}>
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">New analysis</span>
            </Button>
            {activeTab === "hub" && selectedAnalyses.size > 0 && (
              <Button
                variant="gradient"
                size="sm"
                className="gap-2"
                onClick={() => setCreateDashboardDialogOpen(true)}
              >
                <Plus className="h-4 w-4" />
                Create dashboard ({selectedAnalyses.size})
              </Button>
            )}
          </div>
        </div>

        {/* Inline success/error message */}
        {reportingMessage && (
          <div className={`flex items-center gap-3 p-4 rounded-lg border ${
            reportingMessage.type === "success" ? "bg-success/10 border-success/20" : "bg-destructive/10 border-destructive/20"
          }`}>
            {reportingMessage.type === "success" ? (
              <div className="h-8 w-8 rounded-full bg-success/20 flex items-center justify-center flex-shrink-0">
                <Check className="h-5 w-5 text-success" />
              </div>
            ) : (
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
            )}
            <p className={`flex-1 text-sm ${reportingMessage.type === "success" ? "text-success" : "text-destructive"}`}>
              {reportingMessage.text}
            </p>
            <Button variant="ghost" size="sm" className="flex-shrink-0" onClick={() => setReportingMessage(null)} aria-label="Dismiss">
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="glass-card">
            <TabsTrigger value="hub" className="gap-2">
              <LayoutDashboard className="h-4 w-4" />
              Hub
            </TabsTrigger>
            <TabsTrigger value="automation" className="gap-2">
              <Mail className="h-4 w-4" />
              Automation
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="hub" className="space-y-6">
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <div className="flex flex-wrap items-center gap-2 flex-1">
                {localDashboards.map((dashboard) => (
                  <div key={dashboard.id} className="flex items-center">
                    <Button
                      size="sm"
                      variant={activeWorkflowDashboardId === dashboard.id ? "gradient" : "outline"}
                      onClick={() => setActiveWorkflowDashboardId(dashboard.id)}
                    >
                      {dashboard.name}
                    </Button>
                    {activeWorkflowDashboardId === dashboard.id && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-8 w-8 ml-0.5">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuItem
                            onClick={() => {
                              setRenameDashboardName(dashboard.name);
                              setRenameDashboardError(null);
                              setRenameDashboardDialogOpen(true);
                            }}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleViewDashboard(dashboard)}>
                            <Eye className="h-4 w-4 mr-2" />
                            Full screen
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            disabled={dashboard.id === "overview-default"}
                            onClick={() => {
                              setDashboardToDelete(dashboard);
                              setDeleteDashboardDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                ))}
              </div>
              <Button
                variant="gradient"
                size="sm"
                className="gap-2 shrink-0"
                disabled={isWorkflowSavingDashboard || !dataService}
                onClick={() => void handleCreateNewDashboard()}
              >
                <Plus className="h-4 w-4" />
                New dashboard
              </Button>
            </div>

            {!activeWorkflowDashboard ? (
              <Card className="glass-card">
                <CardContent className="py-10 text-center text-muted-foreground">
                  {localDashboards.length === 0
                    ? "Your overview dashboard will appear here once your data loads."
                    : "Select a dashboard above to get started."}
                </CardContent>
              </Card>
            ) : (
              <Card className="glass-card">
                <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <CardTitle>{activeWorkflowDashboard.name}</CardTitle>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" className="gap-2" onClick={openCreateAnalysisPage}>
                      <Plus className="h-4 w-4" />
                      Create analysis
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => void handleSendDashboardReportNow(activeWorkflowDashboard)}
                    >
                      <Mail className="h-4 w-4" />
                      Email this dashboard
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {activeWorkflowDashboard.analyses.length === 0 ? (
                    <p className="text-muted-foreground text-sm">
                      No charts yet. Create an analysis or add one from your library below.
                    </p>
                  ) : (
                    <div className="grid gap-4 lg:grid-cols-2">
                      {activeWorkflowDashboard.analyses.map((analysis, index) => {
                        const latest = localSavedAnalyses.find((saved) => saved.id === analysis.id) || analysis;
                        return (
                          <Card key={analysis.id} className="border">
                            <CardHeader className="pb-3">
                              <div className="flex items-center justify-between gap-2">
                                <CardTitle className="text-base">{latest.name}</CardTitle>
                                <div className="flex gap-1">
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7"
                                    disabled={index === 0}
                                    onClick={() => moveAnalysisInActiveDashboard(analysis.id, "up")}
                                  >
                                    <ArrowUp className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7"
                                    disabled={index === activeWorkflowDashboard.analyses.length - 1}
                                    onClick={() => moveAnalysisInActiveDashboard(analysis.id, "down")}
                                  >
                                    <ArrowDown className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7 text-destructive"
                                    onClick={() => void handleRemoveFromDashboard(analysis.id)}
                                    aria-label="Remove from dashboard"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <AnalysisChartRenderer
                                analysis={latest}
                                transactions={transactions}
                                gradingOrders={gradingOrders}
                                height={280}
                              />
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Card className="glass-card">
              <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <LayoutGrid className="h-5 w-5" />
                  Analyses library
                </CardTitle>
                {selectedAnalyses.size > 0 && (
                  <Button variant="outline" size="sm" onClick={() => setCreateDashboardDialogOpen(true)}>
                    Create dashboard from selection ({selectedAnalyses.size})
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {localSavedAnalyses.length === 0 ? (
                  <div className="text-center py-8 space-y-3">
                    <Database className="h-10 w-10 mx-auto text-muted-foreground" />
                    <p className="text-muted-foreground text-sm">No saved analyses yet.</p>
                    <Button variant="gradient" size="sm" onClick={() => navigate("/reporting/analysis/new")}>
                      Create your first analysis
                    </Button>
                  </div>
                ) : (
                  <ul className="divide-y">
                    {localSavedAnalyses.map((analysis) => (
                      <li
                        key={analysis.id}
                        className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="flex items-start gap-3 min-w-0">
                          <Checkbox
                            checked={selectedAnalyses.has(analysis.id)}
                            onCheckedChange={() => toggleAnalysisSelection(analysis.id)}
                            className="mt-1"
                          />
                          <div className="min-w-0">
                            <p className="font-medium truncate">
                              {getAnalysisTypeIcon(analysis.chartType)} {analysis.name}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {analysis.metrics.slice(0, 2).join(", ")}
                              {analysis.metrics.length > 2 ? ` +${analysis.metrics.length - 2}` : ""}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 shrink-0">
                          <Button size="sm" variant="outline" onClick={() => handlePreviewAnalysis(analysis)}>
                            Preview
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleEditAnalysis(analysis)}>
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!activeWorkflowDashboard}
                            onClick={() => void handleAddAnalysisToDashboard(analysis)}
                          >
                            Add to dashboard
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive"
                            onClick={() => {
                              setAnalysisToDelete(analysis);
                              setDeleteAnalysisDialogOpen(true);
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="automation" className="space-y-6">
            <Card className="glass-card">
              <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle>Current insights</CardTitle>
                <Button
                  variant="gradient"
                  size="sm"
                  className="gap-2"
                  disabled={insightsSending || !dataService}
                  onClick={() => void handleSendInsightEmailNow()}
                >
                  <Mail className="h-4 w-4" />
                  {insightsSending ? "Sending…" : "Email these insights now"}
                </Button>
              </CardHeader>
              <CardContent className="space-y-6">
                {liveInsights.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No insights for this data set yet.</p>
                ) : (
                  liveInsights.map((insight) => (
                    <div key={insight.id} className="border-b pb-4 last:border-0 last:pb-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="font-semibold text-base">{insight.title}</h3>
                        <Badge
                          variant={insight.severity === "warning" ? "destructive" : "secondary"}
                          className="text-xs capitalize"
                        >
                          {insight.severity}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{insight.summary}</p>
                      <ul className="list-disc pl-5 text-sm space-y-1">
                        {insight.actions.map((a, idx) => (
                          <li key={idx}>{a}</li>
                        ))}
                      </ul>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            <Card className="glass-card">
              <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle>Scheduled emails</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    We&apos;ll email these on the cadence you choose.
                  </p>
                </div>
                <Button variant="gradient" size="sm" onClick={handleAddAutomatedReportJob} disabled={!dataService}>
                  Add scheduled email
                </Button>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-sm text-muted-foreground">Schedule the reports you want us to email you.</p>

                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 items-end">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={newReportType} onValueChange={(v) => setNewReportType(v as AutomatedReportType)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="insights">Insights digest</SelectItem>
                        <SelectItem value="dashboard">Dashboard report</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Frequency</Label>
                    <Select
                      value={newReportFrequency}
                      onValueChange={(v) => setNewReportFrequency(v as AutomatedReportFrequency)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="off">Off</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {newReportType === "dashboard" && (
                    <div className="space-y-2 md:col-span-2">
                      <Label>Dashboard</Label>
                      <Select value={newReportDashboardId} onValueChange={setNewReportDashboardId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select dashboard" />
                        </SelectTrigger>
                        <SelectContent>
                          {localDashboards.map((d) => (
                            <SelectItem key={d.id} value={d.id}>
                              {d.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="flex items-center gap-2 pb-2">
                    <Checkbox
                      id="job-enabled"
                      checked={newReportEnabled}
                      onCheckedChange={(c) => setNewReportEnabled(Boolean(c))}
                    />
                    <Label htmlFor="job-enabled" className="cursor-pointer">
                      Enabled
                    </Label>
                  </div>
                </div>

                <Separator />

                {automatedReportJobs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No scheduled emails yet.</p>
                ) : (
                  <ul className="space-y-3">
                    {automatedReportJobs.map((job) => {
                      const dashName =
                        job.type === "dashboard"
                          ? localDashboards.find((d) => d.id === job.dashboardId)?.name || job.dashboardId
                          : null;
                      return (
                        <li
                          key={job.id}
                          className="flex flex-col gap-2 border rounded-lg p-3 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div>
                            <span className="font-medium">
                              {job.type === "insights" ? "Insights digest" : "Dashboard report"}
                            </span>
                            {job.type === "dashboard" && dashName && (
                              <span className="text-muted-foreground"> — {dashName}</span>
                            )}
                            <div className="text-xs text-muted-foreground">
                              {job.enabled ? "On" : "Off"} · {job.frequency}
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="outline">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => void handleSendJobNow(job)}>
                                Send now
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setEditingReportJob(job)}>
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() =>
                                  void persistAutomatedJobs(automatedReportJobs.filter((j) => j.id !== job.id))
                                }
                              >
                                Remove
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Send a one-time report</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Pick a dashboard and we&apos;ll email you a PDF right away.
                </p>
              </CardHeader>
              <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="space-y-2 flex-1">
                  <Label>Dashboard</Label>
                  <Select value={adhocDashboardId} onValueChange={setAdhocDashboardId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select dashboard" />
                    </SelectTrigger>
                    <SelectContent>
                      {localDashboards.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  variant="gradient"
                  disabled={!adhocDashboardId || !dataService}
                  onClick={() => {
                    const d = localDashboards.find((x) => x.id === adhocDashboardId);
                    if (d) void handleSendDashboardReportNow(d);
                  }}
                >
                  Send PDF
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        {/* Create/Edit Dashboard Dialog */}
        <Dialog open={createDashboardDialogOpen} onOpenChange={(open) => {
          setCreateDashboardDialogOpen(open);
          if (!open) {
            setEditingDashboard(null);
            setNewDashboardName('');
            setSelectedAnalyses(new Set());
            setCreateDashboardError(null);
          }
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingDashboard ? 'Edit Dashboard' : 'Create Dashboard'}</DialogTitle>
              <DialogDescription>
                {editingDashboard 
                  ? `Edit dashboard "${editingDashboard.name}"`
                  : `Create a new dashboard from selected analyses`}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              <div className="space-y-2">
                <Label htmlFor="dashboard-name">Dashboard Name *</Label>
                <Input
                  id="dashboard-name"
                  value={newDashboardName}
                  onChange={(e) => {
                    setNewDashboardName(e.target.value);
                    if (createDashboardError) setCreateDashboardError(null);
                  }}
                  placeholder="Enter dashboard name"
                />
                {createDashboardError && (
                  <p className="text-sm text-destructive">{createDashboardError}</p>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Selected Analyses Section */}
                <div className="space-y-3">
                  <Label className="text-base font-semibold">
                    Selected Analyses ({selectedAnalyses.size})
                  </Label>
                  <div className="border rounded-lg p-4 min-h-[300px] max-h-[400px] overflow-y-auto bg-muted/20">
                    {selectedAnalyses.size === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No analyses selected. Click analyses from "Available Analyses" to add them.
                      </p>
                    ) : (
                      <div className="flex flex-col gap-2">
                        {Array.from(selectedAnalyses).map(analysisId => {
                          const analysis = localSavedAnalyses.find(a => a.id === analysisId);
                          return analysis ? (
                            <div
                              key={analysis.id}
                              className="flex items-center justify-between p-2 border rounded-lg bg-background hover:bg-muted/50 transition-colors cursor-pointer group"
                              onClick={() => handlePreviewAnalysis(analysis)}
                            >
                              <Badge 
                                variant="secondary" 
                                className="flex-1 justify-start cursor-pointer"
                              >
                                <span className="mr-2">{getAnalysisTypeIcon(analysis.chartType)}</span>
                                {analysis.name}
                              </Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedAnalyses(prev => {
                                    const updated = new Set(prev);
                                    updated.delete(analysis.id);
                                    return updated;
                                  });
                                }}
                              >
                                <Trash2 className="h-3 w-3 text-destructive" />
                              </Button>
                            </div>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Available Analyses Section */}
                <div className="space-y-3">
                  <Label className="text-base font-semibold">
                    Available Analyses ({localSavedAnalyses.filter(a => !selectedAnalyses.has(a.id)).length})
                  </Label>
                  <div className="border rounded-lg p-4 min-h-[300px] max-h-[400px] overflow-y-auto bg-muted/20">
                    {localSavedAnalyses.filter(a => !selectedAnalyses.has(a.id)).length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        All analyses are already in this dashboard.
                      </p>
                    ) : (
                      <div className="flex flex-col gap-2">
                        {localSavedAnalyses
                          .filter(a => !selectedAnalyses.has(a.id))
                          .map(analysis => (
                            <div
                              key={analysis.id}
                              className="flex items-center justify-between p-2 border rounded-lg bg-background hover:bg-muted/50 transition-colors cursor-pointer group"
                              onClick={() => handlePreviewAnalysis(analysis)}
                            >
                              <Badge 
                                variant="outline" 
                                className="flex-1 justify-start cursor-pointer"
                              >
                                <span className="mr-2">{getAnalysisTypeIcon(analysis.chartType)}</span>
                                {analysis.name}
                              </Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedAnalyses(prev => new Set(prev).add(analysis.id));
                                }}
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="text-xs text-muted-foreground border-t pt-3">
                <p>💡 Click on any analysis to preview it before adding or removing it from the dashboard.</p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setCreateDashboardDialogOpen(false);
                setEditingDashboard(null);
                setNewDashboardName('');
                setSelectedAnalyses(new Set());
              }}>
                Cancel
              </Button>
              <Button
                variant="gradient"
                onClick={handleCreateDashboard}
                disabled={!newDashboardName.trim() || selectedAnalyses.size === 0}
              >
                <Plus className="h-4 w-4 mr-2" />
                {editingDashboard ? 'Update Dashboard' : 'Create Dashboard'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        <Dialog open={renameDashboardDialogOpen} onOpenChange={(open) => {
          setRenameDashboardDialogOpen(open);
          if (!open) setRenameDashboardError(null);
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Rename dashboard</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <Label htmlFor="rename-dashboard">Name</Label>
              <Input
                id="rename-dashboard"
                value={renameDashboardName}
                onChange={(e) => {
                  setRenameDashboardName(e.target.value);
                  if (renameDashboardError) setRenameDashboardError(null);
                }}
              />
              {renameDashboardError && (
                <p className="text-sm text-destructive">{renameDashboardError}</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRenameDashboardDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="gradient" onClick={() => void handleRenameActiveDashboard()}>
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={Boolean(editingReportJob)} onOpenChange={(open) => !open && setEditingReportJob(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit scheduled email</DialogTitle>
            </DialogHeader>
            {editingReportJob && (
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={editingReportJob.type}
                    onValueChange={(v) =>
                      setEditingReportJob({ ...editingReportJob, type: v as AutomatedReportType })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="insights">Insights digest</SelectItem>
                      <SelectItem value="dashboard">Dashboard report</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Frequency</Label>
                  <Select
                    value={editingReportJob.frequency}
                    onValueChange={(v) =>
                      setEditingReportJob({
                        ...editingReportJob,
                        frequency: v as AutomatedReportFrequency,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="off">Off</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editingReportJob.type === "dashboard" && (
                  <div className="space-y-2">
                    <Label>Dashboard</Label>
                    <Select
                      value={editingReportJob.dashboardId || ""}
                      onValueChange={(v) =>
                        setEditingReportJob({ ...editingReportJob, dashboardId: v })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select dashboard" />
                      </SelectTrigger>
                      <SelectContent>
                        {localDashboards.map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            {d.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="edit-job-enabled"
                    checked={editingReportJob.enabled}
                    onCheckedChange={(c) =>
                      setEditingReportJob({ ...editingReportJob, enabled: Boolean(c) })
                    }
                  />
                  <Label htmlFor="edit-job-enabled" className="cursor-pointer">
                    Enabled
                  </Label>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingReportJob(null)}>
                Cancel
              </Button>
              <Button
                variant="gradient"
                onClick={() => {
                  if (editingReportJob) {
                    void persistAutomatedJobs(
                      automatedReportJobs.map((j) => (j.id === editingReportJob.id ? editingReportJob : j))
                    );
                    setEditingReportJob(null);
                  }
                }}
              >
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Analysis Dialog */}
        <Dialog open={deleteAnalysisDialogOpen} onOpenChange={setDeleteAnalysisDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Delete Analysis</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{analysisToDelete?.name}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteAnalysisDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteAnalysis}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Delete Dashboard Dialog */}
        <Dialog open={deleteDashboardDialogOpen} onOpenChange={setDeleteDashboardDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Delete Dashboard</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{dashboardToDelete?.name}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDashboardDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteDashboard}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Analysis Preview Dialog */}
                        <AnalysisPreviewDialog
                  open={previewDialogOpen}
                  onOpenChange={setPreviewDialogOpen}
                  analysis={analysisToPreview}
                  transactions={transactions}
                  gradingOrders={gradingOrders}
                  expenses={expenses}
                  onChartTypeChange={handleChartTypeChange}
                  getValidChartTypes={getValidChartTypes}
                  getAnalysisTypeIcon={getAnalysisTypeIcon}
                  onExportData={analysisToPreview ? () => handleExportAnalysisData(analysisToPreview) : undefined}
                  onExportPDF={analysisToPreview ? (chartElement) => handleExportAnalysisPDF(analysisToPreview, chartElement) : undefined}
                  onError={(msg) => showReportingMessage('error', msg)}
                />
        
        {/* Dashboard View Dialog */}
                        <DashboardViewDialog
                  open={dashboardViewDialogOpen}
                  onOpenChange={setDashboardViewDialogOpen}
                  dashboard={dashboardToView}
                  transactions={transactions}
                  gradingOrders={gradingOrders}
                  expenses={expenses}
                  savedAnalyses={localSavedAnalyses}
                  onChartTypeChange={handleChartTypeChange}
                  getValidChartTypes={getValidChartTypes}
                  getAnalysisTypeIcon={getAnalysisTypeIcon}
                  onExportData={dashboardToView ? () => handleExportDashboardData(dashboardToView) : undefined}
                  onExportPDF={dashboardToView ? (chartElements) => handleExportDashboardPDF(dashboardToView, chartElements) : undefined}
                  onError={(msg) => showReportingMessage('error', msg)}
                />
      </main>
    </div>
  );
};

export default Reporting;
